import { useState } from 'react';
import { useSession } from '../lib/session';
import { supabase } from '../lib/supabase';
import { BrandStripe } from '../components/BrandStripe';
import { Button, Cluster, Stack, Text } from '../components/ui';

const BENEFITS = [
  'The full archive of every piece',
  'Every drawer in the closet',
  'Live calls and member Q+As',
  'The Saleroom auctions',
  'Member-only events and drops',
];

/**
 * Paywall route for members without active access. Posts CTA to
 * /api/stripe/checkout which returns a hosted checkout URL.
 * Admin/developer/comped users bypass this entirely (see RequireAccess).
 *
 * Reworked to echo the SignIn split: the BrandStripe (spinning logo on the
 * carbon/accent panel) on the left, a clean paper offer card on the right -
 * so the copy sits on paper (high contrast) instead of over the accent field.
 */
export default function Subscribe() {
  const { profile } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout() {
    setBusy(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) { setError('Sign in first.'); return; }

      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data?.url) {
        setError(data?.error ?? 'Checkout failed.');
        return;
      }
      window.location.assign(data.url);
    } catch (err: any) {
      setError(err?.message ?? 'Checkout failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-ds className="flex min-h-[70dvh] items-center justify-center px-6 py-12">
      <div className="grid w-full max-w-[920px] overflow-hidden rounded-lg border border-border bg-surface shadow-card md:grid-cols-[0.85fr_1fr]">
        <BrandStripe className="min-h-[200px] md:min-h-[460px]" logoSize="min(64%, 300px)" />

        <div className="flex items-center p-8 sm:p-12">
          <Stack gap="loose" className="w-full">
            <p className="kicker">Conversion Club</p>

            <div className="flex items-end gap-2">
              <span className="font-display font-[800] leading-none text-ink text-[clamp(3rem,7vw,4.5rem)] [font-variant-numeric:tabular-nums]">
                $15
              </span>
              <Text role="caption" muted as="span" className="pb-2">/ month</Text>
            </div>

            <Text role="body" prose>
              The room behind the Instagram. One tier, everything included.
            </Text>

            <Stack gap="snug" as="ul" className="list-none">
              {BENEFITS.map((b) => (
                <Cluster as="li" key={b} gap="tight" align="start" wrap={false}>
                  <span aria-hidden="true" className="mt-px font-bold text-accent">✓</span>
                  <Text role="small">{b}</Text>
                </Cluster>
              ))}
            </Stack>

            <Stack gap="tight">
              <Button variant="accent" size="md" fullWidth loading={busy} onClick={startCheckout} disabled={!profile}>
                Join the Club
              </Button>
              <Text role="caption" muted className="text-center">
                Secure checkout via Stripe. Cancel anytime.
              </Text>
              {!profile && (
                <Text role="caption" muted className="text-center">Sign in to subscribe.</Text>
              )}
            </Stack>

            {error && <Text role="small" className="text-danger">{error}</Text>}
          </Stack>
        </div>
      </div>
    </div>
  );
}
